import { ref, onBeforeUnmount, type ShallowRef, type Ref } from 'vue';
import * as Cesium from 'cesium';
import type { SelectedAsset } from '@/stores/detailPanel'

export function useHoverEffect(viewer: ShallowRef<Cesium.Viewer | null, Cesium.Viewer | null>, selectedAsset: Ref<SelectedAsset | null>) {
    const highlightedFeature = ref<Cesium.Cesium3DTileFeature | null | undefined>(null);
    const currentTileset = ref<Cesium.Cesium3DTileset | null>(null);

    let handler: Cesium.ScreenSpaceEventHandler | null = null
    let isThrottled = false;

    const throttle = <T extends (...args: any[]) => void>(
        func: T,
        delay: number,
    ) => {
        return (...args: Parameters<T>) => {
            if (!isThrottled) {
                func(...args)
                isThrottled = true
                setTimeout(() => {
                    isThrottled = false
                }, delay)
            }
        }
    }

    // Check if a feature is currently selected
    const isSelected = (feature: Cesium.Cesium3DTileFeature | null | undefined) => {
        if (!selectedAsset.value || !feature) return false;

        // Compare by ID or other unique property
        const featureContent = feature.featureId;
        const selectedContent = selectedAsset.value?.feature.featureId;

        return featureContent === selectedContent;
    };

    // Reset highlight on previous feature
    const resetHighlight = () => {
        if (highlightedFeature.value) {
            // Don't reset color if this is the selected feature

            if (!isSelected(highlightedFeature.value)) {
                highlightedFeature.value.color = Cesium.Color.WHITE;
            }
            viewer.value?.scene.requestRender()

            highlightedFeature.value = null;
            currentTileset.value = null;
        }
    };

    // Apply hover effect
    const applyHoverEffect = (feature: Cesium.Cesium3DTileFeature, tileset: Cesium.Cesium3DTileset | null) => {
        // Don't apply hover effect if this is the selected feature
        if (isSelected(feature)) {
            viewer.value && (viewer.value.canvas.style.cursor = 'pointer');
            return;
        }

        // Apply hover color with transparency
        feature.color = Cesium.Color.YELLOW.withAlpha(0.7);
        viewer.value?.scene.requestRender()

        highlightedFeature.value = feature;
        currentTileset.value = tileset;
    };

    // Handle mouse move with throttling
    const handleMouseMove = throttle((movement) => {
        if (!viewer.value) return;

        const pickedObject = viewer.value.scene.pick(movement.endPosition);

        // Check if we picked a valid 3D Tile feature
        if (Cesium.defined(pickedObject) && pickedObject instanceof Cesium.Cesium3DTileFeature) {
            const pickedTileset = pickedObject.tileset;

            if (pickedTileset) {
                // Only update if it's a different feature  
                if (!highlightedFeature.value) {
                    applyHoverEffect(pickedObject, pickedTileset);
                    viewer.value.canvas.style.cursor = 'pointer';
                } else if (highlightedFeature.value && highlightedFeature.value.featureId !== pickedObject.featureId) {
                    resetHighlight();
                    applyHoverEffect(pickedObject, pickedTileset);
                    viewer.value.canvas.style.cursor = 'pointer';
                }
            }
        } else {
            // Mouse is not over any feature
            if (highlightedFeature.value) {
                resetHighlight();
                viewer.value.canvas.style.cursor = 'default';
            }
        }
    }, 16);

    const initializeHover = () => {
        if (!viewer.value) {
            console.warn('Viewer not initialized');
            return;
        }

        // Setup mouse move handler
        handler = new Cesium.ScreenSpaceEventHandler(viewer.value.scene.canvas);
        handler.setInputAction(handleMouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    };

    onBeforeUnmount(() => {
        resetHighlight();
        if (handler) {
            handler.destroy();
            handler = null;
        }
        if (viewer.value) {
            viewer.value.canvas.style.cursor = 'default';
        }
    });

    return {
        highlightedFeature,
        currentTileset,
        initializeHover,
        resetHighlight
    };
}